import { createSelector } from '@reduxjs/toolkit';

const selectAppState = (state) => state.app;
const selectFiltersState = (state) => state.filters;
const selectLandingState = (state) => state.landing;
const selectRmState = (state) => state.rm;
const selectScenarioOneState = (state) => state.scenarioOne;
const selectScenarioTwoState = (state) => state.scenarioTwo;
const selectScenarioThreeState = (state) => state.scenarioThree;
const selectScenarioFourState = (state) => state.scenarioFour;
const selectMarketState = (state) => state.market;
const selectTeamState = (state) => state.team;

/************************************************************************************
 * Common Selectors *
 ***********************************************************************************/
export const selectAuthenticateStatus = createSelector(
    [selectAppState],
    (app) => app.isAuthenticated
);
export const selectLoggedInUser = createSelector(
    [selectAppState],
    (app) => app.user
);
export const selectShowRmInvestigationOverlay = createSelector(
    [selectAppState],
    (app) => app.showRmInvestigationOverlay
);
export const selectShowOverallAppOverlay = createSelector(
    [selectAppState],
    (app) => app.showOverallAppOverlay
);
export const selectShowUserProfileOverlay = createSelector(
    [selectAppState],
    (app) => app.showUserProfileOverlay
);
export const selectLastRefreshDate = createSelector(
    [selectAppState],
    (app) => app.lastRefreshDate
);
export const selectSettings = createSelector(
    [selectAppState],
    (app) => app.settings
);
export const selectZoneValue = createSelector(
    [selectFiltersState],
    (filters) => filters.zone.value
);
export const selectMarketValue = createSelector(
    [selectFiltersState],
    (filters) => filters.market.value
);
export const selectTeamValue = createSelector(
    [selectFiltersState],
    (filters) => filters.team.value
);
export const selectRmValue = createSelector(
    [selectFiltersState],
    (filters) => filters.rm.value
);
export const selectZoneLabel = createSelector(
    [selectFiltersState],
    (filters) => filters.zone.label
);
export const selectMarketLabel = createSelector(
    [selectFiltersState],
    (filters) => filters.market.label
);
export const selectTeamLabel = createSelector(
    [selectFiltersState],
    (filters) => filters.team.label
);
export const selectRmLabel = createSelector(
    [selectFiltersState],
    (filters) => filters.rm.label
);
export const selectMarketStat = createSelector(
    [selectFiltersState],
    (filters) => filters.market.stat
);
export const selectTeamStat = createSelector(
    [selectFiltersState],
    (filters) => filters.team.stat
);
export const selectRmStat = createSelector(
    [selectFiltersState],
    (filters) => filters.rm.stat
);
export const selectZoneDropdownValues = createSelector(
    [selectFiltersState],
    (filters) => filters.zone.dropdownValues
);
export const selectMarketDropdownValues = createSelector(
    [selectFiltersState],
    (filters) => filters.market.dropdownValues
);
export const selectTeamDropdownValues = createSelector(
    [selectFiltersState],
    (filters) => filters.team.dropdownValues
);
export const selectRmDropdownValues = createSelector(
    [selectFiltersState],
    (filters) => filters.rm.dropdownValues
);
export const selectFiltersIsFetched = createSelector(
    [selectFiltersState],
    (filters) => filters.filtersInitialFetch
);
export const selectZonesFetched = createSelector(
    [selectFiltersState],
    (filters) => filters.zonesFetched
);
export const selectFiltersAllValues = createSelector(
    [selectFiltersState],
    (filters) => filters.allValues
);
export const selectFiltersTeamAllValue = createSelector(
    [selectFiltersState],
    (filters) => filters.allValues.team.value
);
export const selectFiltersRmAllValue = createSelector(
    [selectFiltersState],
    (filters) => filters.allValues.rm.value
);
export const selectTimeslicerMonthList = createSelector(
    [selectFiltersState],
    (filters) => filters.timeSlicer.months
);
export const selectTimeslicerStartMonth = createSelector(
    [selectFiltersState],
    (filters) => filters.timeSlicer.start
);
export const selectTimeslicerEndMonth = createSelector(
    [selectFiltersState],
    (filters) => filters.timeSlicer.end
);
export const selectTimeslicerIsSameMonth = createSelector(
    [selectFiltersState],
    (filters) => filters.timeSlicer.isSameMonth
);
export const selectFiltersDisplayMapping = createSelector(
    [selectFiltersState],
    (filters) => filters.displayMapping
);

/************************************************************************************
 * Landing Page Selectors *
 ***********************************************************************************/
export const selectLandingStats = createSelector(
    [selectLandingState],
    (landing) => landing.stats
);
export const selectTopThreeMarketsData = createSelector(
    [selectLandingState],
    (landing) => landing.topThreeMarketsData
);
export const selectTopFiveTeamsData = createSelector(
    [selectLandingState],
    (landing) => landing.topFiveTeamsData
);
export const selectTopFiveRmsData = createSelector(
    [selectLandingState],
    (landing) => landing.topFiveRmsData
);
export const selectTopFiveSpikeData = createSelector(
    [selectLandingState],
    (landing) => landing.topFiveRmsSpike
);
export const selectTopFiveEmergingModerateRmsData = createSelector(
    [selectLandingState],
    (landing) => landing.topFiveEmergingModerateRm
);
export const selectTopFiveEmergingMajorRmsData = createSelector(
    [selectLandingState],
    (landing) => landing.topFiveEmergingMajorRm
);
export const selectMinorThreshold = createSelector(
    [selectLandingState],
    (landing) => landing.minorThreshold
);
export const selectMajorThreshold = createSelector(
    [selectLandingState],
    (landing) => landing.majorThreshold
);

/************************************************************************************
 * Scenario One Selectors *
 ***********************************************************************************/
export const selectClientSelected = createSelector(
    [selectScenarioOneState],
    (scenarioOne) => scenarioOne.clientSelected
);
export const selectDataIndex = createSelector(
    [selectScenarioOneState],
    (scenarioOne) => scenarioOne.selectedDataIndex
);
export const selectSeriesIndex = createSelector(
    [selectScenarioOneState],
    (scenarioOne) => scenarioOne.selectedSeriesIndex
);
export const selectScenarioOneStats = createSelector(
    [selectScenarioOneState],
    (scenarioOne) => scenarioOne.stats
);
export const selectScenarioOneOutliers = createSelector(
    [selectScenarioOneState],
    (scenarioOne) => scenarioOne.outlierData
);
export const selectScenarioOneTrends = createSelector(
    [selectScenarioOneState],
    (scenarioOne) => scenarioOne.trendData
);
export const selectScenarioOnePortfolios = createSelector(
    [selectScenarioOneState],
    (scenarioOne) => scenarioOne.portfolioData
);

/************************************************************************************
 * Scenario Two Selectors *
 ***********************************************************************************/
export const selectScenarioTwoStats = createSelector(
    [selectScenarioTwoState],
    (scenarioTwo) => scenarioTwo.stats
);
export const selectScenarioTwoOutliersData = createSelector(
    [selectScenarioTwoState],
    (scenarioTwo) => scenarioTwo.outliersData
);
export const selectScenarioTwoBreachCategoryData = createSelector(
    [selectScenarioTwoState],
    (scenarioTwo) => scenarioTwo.breachCategoryData
);
export const selectScenarioTwoTrendData = createSelector(
    [selectScenarioTwoState],
    (scenarioTwo) => scenarioTwo.trendData
);
export const selectScenarioTwoTopFiveRmsData = createSelector(
    [selectScenarioTwoState],
    (scenarioTwo) => scenarioTwo.topFiveRmsData
);

/************************************************************************************
 * Scenario Three Selectors *
 ***********************************************************************************/
export const selectScenarioThreeOutlierPage = createSelector(
    [selectScenarioThreeState],
    (scenarioThree) => scenarioThree.outlierChartPage
);
export const selectScenarioThreeOutlierData = createSelector(
    [selectScenarioThreeState],
    (scenarioThree) => scenarioThree.outlierData
);
export const selectScenarioThreeVcData = createSelector(
    [selectScenarioThreeState],
    (scenarioThree) => scenarioThree.vcData
);
export const selectScenarioThreeStats = createSelector(
    [selectScenarioThreeState],
    (scenarioThree) => scenarioThree.stats
);
export const selectScenarioThreeTrendData = createSelector(
    [selectScenarioThreeState],
    (scenarioThree) => scenarioThree.trendData
);

/************************************************************************************
 * Market Selectors *
 ***********************************************************************************/
export const selectMarketViewStats = createSelector(
    [selectMarketState],
    (market) => market.statsReporting
);
export const selectMarketViewTopFiveTeamsData = createSelector(
    [selectMarketState],
    (market) => market.topFiveTeamsData
);
export const selectMarketViewTopFiveRmsData = createSelector(
    [selectMarketState],
    (market) => market.topFiveRmsData
);
export const selectMarketViewPeerComparisonChartData = createSelector(
    [selectMarketState],
    (market) => market.peerComparisonChartData
);
export const selectMarketViewTrendAnalysisChartData = createSelector(
    [selectMarketState],
    (market) => market.trendAnalysisChartData
);
export const selectMarketViewTopFiveSpikeData = createSelector(
    [selectMarketState],
    (market) => market.topFiveRmsSpike
);
export const selectMarketViewTopFiveEmergingModerateRmsData = createSelector(
    [selectMarketState],
    (market) => market.topFiveEmergingModerateRm
);
export const selectMarketViewTopFiveEmergingMajorRmsData = createSelector(
    [selectMarketState],
    (market) => market.topFiveEmergingMajorRm
);
export const selectMarketViewMinorThreshold = createSelector(
    [selectMarketState],
    (market) => market.minorThreshold
);
export const selectMarketViewMajorThreshold = createSelector(
    [selectMarketState],
    (market) => market.majorThreshold
);

/************************************************************************************
 * Team Selectors *
 ***********************************************************************************/
export const selectTeamViewStats = createSelector(
    [selectTeamState],
    (team) => team.statsReporting
);
export const selectTeamViewTopFiveRmsData = createSelector(
    [selectTeamState],
    (team) => team.topFiveRmsData
);
export const selectTeamViewPeerComparisonChartData = createSelector(
    [selectTeamState],
    (team) => team.peerComparisonChartData
);
export const selectTeamViewTrendAnalysisChartData = createSelector(
    [selectTeamState],
    (team) => team.trendAnalysisChartData
);
export const selectTeamViewTopFiveSpikeData = createSelector(
    [selectTeamState],
    (team) => team.topFiveRmsSpike
);
export const selectTeamViewTopFiveEmergingModerateRmsData = createSelector(
    [selectTeamState],
    (team) => team.topFiveEmergingModerateRm
);
export const selectTeamViewTopFiveEmergingMajorRmsData = createSelector(
    [selectTeamState],
    (team) => team.topFiveEmergingMajorRm
);
export const selectTeamViewMinorThreshold = createSelector(
    [selectTeamState],
    (team) => team.minorThreshold
);
export const selectTeamViewMajorThreshold = createSelector(
    [selectTeamState],
    (team) => team.majorThreshold
);

/************************************************************************************
 * Rm Page Selectors *
 ***********************************************************************************/
export const selectRmStatsReporting = createSelector(
    [selectRmState],
    (rm) => rm.stats
);
export const selectRmTrendData = createSelector(
    [selectRmState],
    (rm) => rm.trendData
);
export const selectRmPeerComparisonData = createSelector(
    [selectRmState],
    (market) => market.peerComparisonData
);
export const selectRmInvestigationData = createSelector(
    [selectRmState],
    (rm) => rm.rmInvestigationData
);
export const selectRmInvestigationSubmitted = createSelector(
    [selectRmState],
    (rm) => rm.rmInvestigationSubmitted
);
export const selectRmInvestigationSelected = createSelector(
    [selectRmState],
    (rm) => rm.rmInvestigationSelected
);
export const selectRmScoreDecompositionData = createSelector(
    [selectRmState],
    (rm) => rm.scoreDecompositionData
);

/************************************************************************************
 * Scenario Four Selectors *
 ***********************************************************************************/
export const selectSelectedRm = createSelector(
    [selectScenarioFourState],
    (state) => state.selectedOutlier
);

export const selectScenarioFourStats = createSelector(
    [selectScenarioFourState],
    (scenarioFour) => scenarioFour.stats
);
export const selectScenarioFourOutlierData = createSelector(
    [selectScenarioFourState],
    (scenarioFour) => scenarioFour.outlierData
);

export const selectScenarioFourSelectedRM = createSelector(
    [selectScenarioFourState],
    (scenarioFour) => scenarioFour.selectedRM
);
